import { useEffect, useRef } from 'react'

export default () => {}

/**
 *
 * containerRef and triggerRef should be defined before calling this hook, so make a parent component with divs before
 * calling this hook...
 *
 * @param containerRef this should be already defined before calling this hook!!!
 * @param triggerRef this should be already defined before calling this hook!!!
 * @param callbackRef pass the callback as a ref because the state of useReducer gets out of sync if just defined as a closure
 */
export function useInfiniteScroll(
  containerRef,
  triggerRef,
  callbackRef,
  IntersectionObserver,
  ResizeObserver,
  rerenderInfiniteScroll,
  setRerenderInfiniteScroll
) {
  const isFetchingRef = useRef()
  const previousContainerHeightRef = useRef()

  useEffect(() => {
    // I need to set the values here in useEffect because useEffect is run after renders, so at this time
    // containerRef and triggerRef are defined
    isFetchingRef.current = false
    previousContainerHeightRef.current = containerRef.current.getBoundingClientRect().height
    /**
     * The "intersectionObserver" fires the callback one time, and disables the flag "isFetchingRef" so that the
     * callback is not fired several times
     *
     * The "resizeObserver" enables the flag "isFetchingRef" so that the callback can be called again
     *
     * By using two observers, I can make sure that the callback is called one time. I tried debouncing, but it is not
     * possible to be in sync with rendering so sometimes, the callback is called two times, which leads to duplicate values
     */
    const intersectionObserverOptions = {
      root: null, // Use the whole screen as scroll area
      rootMargin: '0px', // Do not grow or shrink the root area
      threshold: 1.0, // Threshold of 1.0 will fire callback when 100% of element is visible
    }
    const intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // isIntersecting means that it entered the root. In our case root is the screen
        if (entry.isIntersecting && !isFetchingRef.current) {
          isFetchingRef.current = true
          callbackRef.current()
        }
      })
    }, intersectionObserverOptions)
    intersectionObserver.observe(triggerRef.current)

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (Math.ceil(previousContainerHeightRef.current) < Math.ceil(entry.contentRect.height)) {
          previousContainerHeightRef.current = entry.contentRect.height
          isFetchingRef.current = false
        }
      })
    })
    resizeObserver.observe(containerRef.current)

    return () => {
      intersectionObserver.disconnect()
      resizeObserver.disconnect()
    }
  }, [
    IntersectionObserver,
    ResizeObserver,
    callbackRef,
    containerRef,
    rerenderInfiniteScroll,
    setRerenderInfiniteScroll,
    triggerRef,
  ])

  return { isFetchingRef, previousContainerHeightRef } // Exposed for tests!!!
}
